import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Tag, Typography, message } from 'antd';
import { getSMSConfig, updateSMSConfig, type AdminSMSConfig } from '../services/api';

const SMSConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSMSConfig();
      if (res.code !== 0) {
        message.error(res.message || '加载短信配置失败');
        return;
      }
      const config = res.data as AdminSMSConfig;
      setHasSecret(Boolean(config.has_secret_key));
      form.setFieldsValue({
        base_url: config.base_url || '',
        access_key: config.access_key || '',
        secret_key: config.secret_key || '',
        sign_name: config.sign_name || '',
        template_id: config.template_id || '',
      });
    } catch (error) {
      message.error('加载短信配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const onFinish = async (values: {
    base_url: string;
    access_key: string;
    secret_key?: string;
    sign_name?: string;
    template_id?: string;
  }) => {
    setSaving(true);
    try {
      const payload = {
        base_url: (values.base_url || '').trim(),
        access_key: (values.access_key || '').trim(),
        secret_key: (values.secret_key || '').trim(),
        sign_name: (values.sign_name || '').trim(),
        template_id: (values.template_id || '').trim(),
      };

      const res = await updateSMSConfig(payload);
      if (res.code !== 0) {
        message.error(res.message || '保存短信配置失败');
        return;
      }

      message.success('短信配置已保存');
      const saved = res.data as AdminSMSConfig | undefined;
      setHasSecret(Boolean(saved?.has_secret_key));
      form.setFieldsValue({
        base_url: saved?.base_url || payload.base_url,
        access_key: saved?.access_key || payload.access_key,
        secret_key: saved?.secret_key || payload.secret_key || '',
        sign_name: saved?.sign_name || payload.sign_name,
        template_id: saved?.template_id || payload.template_id,
      });
    } catch (error) {
      message.error('保存短信配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        短信接口配置
      </Typography.Title>

      <Card loading={loading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="保存后后端会立即使用新配置调用短信验证码接口，无需重启服务。"
          />

          <div>
            当前密钥状态：
            {hasSecret ? <Tag color="green">已配置 SecretKey</Tag> : <Tag color="red">未配置 SecretKey</Tag>}
          </div>

          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="短信基础地址"
              name="base_url"
              rules={[{ required: true, message: '请输入短信基础地址' }]}
            >
              <Input placeholder="例如：https://xxx.example.com 或 https://xxx.example.com/v3" />
            </Form.Item>

            <Form.Item
              label="AccessKey"
              name="access_key"
              rules={[{ required: true, message: '请输入 AccessKey' }]}
            >
              <Input placeholder="请输入 access_key" />
            </Form.Item>

            <Form.Item
              label="SecretKey"
              name="secret_key"
              extra="留空表示保留当前密钥，不会覆盖。"
            >
              <Input.Password placeholder="请输入新的 secret_key（可留空）" />
            </Form.Item>

            <Form.Item label="签名名称（可选）" name="sign_name">
              <Input placeholder="预留字段，可选填写" />
            </Form.Item>

            <Form.Item label="模板ID（可选）" name="template_id">
              <Input placeholder="预留字段，可选填写" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存配置
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default SMSConfig;
